import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Button,
  Icon,
  Message,
  Checkbox,
} from 'semantic-ui-react';
import {
  useLazyValidateInviteTokenQuery,
  useSubmitOnboardingMutation,
} from '../contractors/contractorsApiSlice';
import { setCredentials, logOut } from '../auth/authSlice';
import { useGetMeQuery } from '../auth/authApiSlice';
import { rememberInviteToken, rememberedInviteToken } from '../auth/inviteToken';
import { VERSION as EULA_VERSION } from '../../legal/meta';
import LegalDocument from '../../legal/LegalDocument';
import { TERMS } from '../../legal/terms';
import { PRIVACY } from '../../legal/privacy';
import { CONSENTS, allConsented } from './consents';
import OnboardingStepper from './OnboardingStepper';
import styles from './OnboardingPage.module.css';
import { BRAND } from '../../brand';

export const onboardingStages = (anonymize) =>
  anonymize ? ['invite', 'eula'] : ['invite', 'profile', 'eula'];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);

  const [stage, setStage] = useState('invite');
  const [inviteToken, setInviteToken] = useState(searchParams.get('token') || rememberedInviteToken() || '');
  const [contractorInfo, setContractorInfo] = useState(null);
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [consents, setConsents] = useState({});
  const [error, setError] = useState('');

  const [validateToken, { isLoading: isValidating }] = useLazyValidateInviteTokenQuery();
  const [submitOnboarding, { isLoading: isSubmitting }] = useSubmitOnboardingMutation();

  // The stored user can be stale: a browser that logged in before onboarding still says
  // "not onboarded" after it was finished elsewhere, and kept landing here (2026-10-21).
  // Ask the server; if it's done, refresh the stored user and move on.
  const { data: me } = useGetMeQuery(undefined, { skip: !token, refetchOnMountOrArgChange: true });
  useEffect(() => {
    if (!me?.onboarding_completed) return;
    dispatch(setCredentials({ access: token, user: me, username: me.username, partner: me.partner }));
    navigate(me.is_approved ? '/landing' : '/pending-approval', { replace: true });
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps

  // Token from the URL, or the one remembered across a logout (#199): auto-validate
  useEffect(() => {
    rememberInviteToken(searchParams.get('token'));  // an invite opened while already signed in
    if (inviteToken && !contractorInfo) {
      handleValidateToken();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleValidateToken = async () => {
    setError('');
    if (!inviteToken.trim()) {
      setError('초대 링크 토큰을 입력해주세요.');
      return;
    }
    try {
      const result = await validateToken(inviteToken.trim()).unwrap();
      setContractorInfo(result);
      setStage(result.anonymize_pii ? 'eula' : 'profile');
    } catch (err) {
      setError(err?.data?.detail || '유효하지 않은 초대 링크입니다.');
    }
  };

  const handleSubmitProfile = () => {
    if (!fullName.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    setError('');
    setStage('eula');
  };

  const handleSubmitOnboarding = async () => {
    if (!allConsented(consents)) {
      setError('필수 항목에 모두 동의해주세요.');
      return;
    }
    setError('');
    try {
      const updatedUser = await submitOnboarding({
        invite_token: inviteToken.trim(),
        full_name: contractorInfo?.anonymize_pii ? '' : fullName.trim(),
        eula_accepted: true,
        eula_version: EULA_VERSION,
      }).unwrap();

      // Update stored user data
      dispatch(setCredentials({
        access: token,
        user: updatedUser,
        username: updatedUser.username,
        partner: updatedUser.partner,
      }));

      navigate(updatedUser.is_approved ? '/landing' : '/pending-approval');
    } catch (err) {
      setError(err?.data?.detail || '온보딩 처리 중 오류가 발생했습니다.');
    }
  };

  const handleLogout = () => {
    dispatch(logOut());
    navigate('/login');
  };

  const STEP_META = {
    invite: { key: 'invite', title: '초대 링크', description: '파트너 초대 확인' },
    profile: { key: 'profile', title: '개인 정보', description: '기본 정보 입력' },
    eula: { key: 'eula', title: '이용 약관', description: '약관 동의' },
  };
  const visibleStages = onboardingStages(contractorInfo?.anonymize_pii);
  const steps = visibleStages.map((s) => STEP_META[s]);
  const activeIndex = visibleStages.indexOf(stage);

  return (
    <div className={styles.page}>
      {/* Fixed top bar */}
      <div className={styles.topbar}>
        <div className={styles.brand}>
          <img src="/ace-logo.png" alt={BRAND} className={styles.logo} />
          {BRAND}
        </div>
        <button className={styles.signout} onClick={handleLogout}>
          <Icon name="sign out" />
          로그아웃
        </button>
      </div>

      {/* Centered stage wrapper */}
      <div className={styles.stage}>
        {/* Frosted invite chip */}
        {contractorInfo?.contractor_name && (
          <div className={styles.invite}>
            <div className={styles.badge}>
              <span className={styles.badgeIc}>
                {contractorInfo.contractor_name.charAt(0).toUpperCase()}
              </span>
              <strong className={styles.badgeName}>{contractorInfo.contractor_name}</strong>
              <span className={styles.badgeLabel}>파트너 초대</span>
            </div>
          </div>
        )}

        {/* Main card */}
        <div className={styles.card}>
          {/* Custom stepper */}
          <OnboardingStepper steps={steps} activeIndex={activeIndex} />

          {error && <Message error>{error}</Message>}

          {/* Stage: invite */}
          {stage === 'invite' && (
            <div className={styles.stageBody}>
              <div className={styles.head}>
                <h2>초대 링크 확인</h2>
                <p>파트너로부터 받은 초대 링크의 토큰을 입력하거나, 초대 링크를 통해 접속해주세요.</p>
              </div>
              <Form>
                <div className={styles.field}>
                  <label>초대 토큰</label>
                  <input
                    type="text"
                    placeholder="초대 토큰을 입력하세요"
                    value={inviteToken}
                    onChange={(e) => setInviteToken(e.target.value)}
                  />
                </div>
                <div className={styles.actions}>
                  <Button
                    className={styles.primaryButton}
                    onClick={handleValidateToken}
                    loading={isValidating}
                    disabled={!inviteToken.trim()}
                  >
                    확인
                  </Button>
                </div>
              </Form>
            </div>
          )}

          {/* Stage: profile */}
          {stage === 'profile' && (
            <div className={styles.stageBody}>
              <div className={styles.head}>
                <h2>정보를 입력해 주세요</h2>
                <p>서비스에서 사용할 이름을 알려 주세요.</p>
              </div>
              <Form>
                <div className={styles.field}>
                  <label>이름</label>
                  <input
                    type="text"
                    placeholder="이름을 입력하세요"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>
                <div className={styles.actions}>
                  <Button className={styles.ghostButton} onClick={() => setStage('invite')}>이전</Button>
                  <Button
                    className={styles.primaryButton}
                    onClick={handleSubmitProfile}
                    disabled={!fullName.trim()}
                  >
                    다음
                  </Button>
                </div>
              </Form>
            </div>
          )}

          {/* Stage: eula */}
          {stage === 'eula' && (
            <div className={styles.stageBody}>
              <div className={styles.head}>
                <h2>약관 동의</h2>
                <p>서비스 이용을 위해 아래 내용을 읽고 각 항목에 동의해주세요.</p>
              </div>

              <div className={styles.docScroll}>
                <h3 className={styles.docTitle}>{TERMS.title}</h3>
                <LegalDocument doc={TERMS} idPrefix="terms-" />
                <h3 className={styles.docTitle}>{PRIVACY.title}</h3>
                <LegalDocument doc={PRIVACY} idPrefix="privacy-" />
              </div>

              <div className={styles.consentList}>
                {CONSENTS.map((c) => (
                  <div key={c.key} className={styles.consentItem}>
                    <Checkbox
                      label={c.label}
                      checked={consents[c.key] === true}
                      onChange={(e, { checked }) =>
                        setConsents((prev) => ({ ...prev, [c.key]: checked }))
                      }
                    />
                  </div>
                ))}
              </div>

              <div className={styles.actions}>
                <Button
                  className={styles.ghostButton}
                  onClick={() => setStage(contractorInfo?.anonymize_pii ? 'invite' : 'profile')}
                >
                  이전
                </Button>
                <Button
                  className={styles.primaryButton}
                  onClick={handleSubmitOnboarding}
                  loading={isSubmitting}
                  disabled={!allConsented(consents)}
                >
                  완료
                </Button>
              </div>
            </div>
          )}

          <p className={styles.footnote}>안전하게 암호화되어 전송됩니다 · {BRAND}</p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
